import { createContext, useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  ConfirmProps,
  ConfirmProviderProps,
  ConfirmState,
} from 'components/ConfirmDialog/ConfirmDialog.types';
import { getDeviceInfo } from 'state/localStorageTyped';

export const ConfirmContext = createContext<ConfirmProps>(() => {
  throw new Error('must be used within a ConfirmProvider');
});

export const ConfirmProvider = ({
  children,
}: ConfirmProviderProps): JSX.Element => {
  const { t } = useTranslation();
  const [state, setState] = useState<ConfirmState | null>(null);
  const deviceInfo = getDeviceInfo();

  const confirm = useCallback<ConfirmProps>(
    (msg, options = {}) =>
      new Promise<boolean>((resolve) => {
        setState({ msg, options, resolve });
      }),
    [],
  );

  const handleClose = (value: boolean): void => {
    state?.resolve(value);
    setState(null);
  };

  const positionSx = deviceInfo?.isMobilePlatform
    ? {}
    : { position: 'fixed', top: 0 };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <Dialog
          maxWidth="xs"
          fullWidth
          open
          onClose={() => handleClose(false)}
          slotProps={{ paper: { sx: positionSx } }}
        >
          {state?.options.title && (
            <DialogTitle>{state.options.title}</DialogTitle>
          )}
          <DialogContent>
            {Array.isArray(state.msg) ? (
              state.msg.map((m) => (
                <DialogContentText
                  key={m}
                  sx={{ mb: 1, color: 'text.primary' }}
                >
                  {m}
                </DialogContentText>
              ))
            ) : (
              <DialogContentText sx={{ color: 'text.primary' }}>
                {state.msg}
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            <Button color="secondary" onClick={() => handleClose(false)}>
              {state?.options.cancelText ?? t('common.cancel')}
            </Button>
            <Button onClick={() => handleClose(true)} autoFocus color="primary">
              {state?.options.okText ?? t('common.ok')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </ConfirmContext.Provider>
  );
};
